<template>
    <div>
        <h5>Позиции поступления ({{ lines.length }})</h5>

        <div v-for="(line, i) in lines" :key="line.key" class="stock-addition-line purchase-receipt-line border rounded p-3 mb-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="font-weight-bold">Позиция {{ i + 1 }}</span>
                <button v-if="lines.length > 1" type="button" class="btn btn-sm btn-outline-danger" @click="removeLine(i)">
                    <i class="fa fa-trash"></i> Удалить
                </button>
            </div>

            <div class="row">
                <div class="col-12 col-xl-6 form-group position-relative">
                    <label>Позиция ТМЦ <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <input
                        class="form-control"
                        :class="{'border-success': line.stock_item_id}"
                        :value="line.item_query"
                        autocomplete="off"
                        placeholder="Начните вводить название"
                        @input="updateItemQuery(line, $event.target.value)"
                        @focus="line.suggestions_open = true"
                        @blur="closeSuggestions(line)"
                    >
                    <input v-if="line.id" type="hidden" :name="'lines[' + i + '][id]'" :value="line.id">
                    <input v-if="line.is_saved" type="hidden" :name="'lines[' + i + '][is_saved]'" value="1">
                    <input type="hidden" :name="'lines[' + i + '][stock_item_id]'" :value="line.stock_item_id">
                    <input type="hidden" :name="'lines[' + i + '][new_item_name]'" :value="line.stock_item_id ? '' : line.item_query">

                    <div v-if="line.suggestions_open && itemSuggestions(line).length" class="purchase-item-suggestions list-group position-absolute w-100 shadow-sm">
                        <button
                            v-for="item in itemSuggestions(line)"
                            :key="item.id"
                            type="button"
                            class="list-group-item list-group-item-action text-left"
                            @mousedown.prevent="selectItem(line, item)"
                        >
                            <strong>{{ item.name }}</strong>
                            <small class="d-block purchase-item-suggestion-meta text-muted">{{ kindLabel(item.kind) }} · {{ trackingLabel(item.tracking_mode) }} · {{ item.unit_name || 'шт.' }}</small>
                        </button>
                    </div>

                    <small v-if="line.stock_item_id" class="form-text text-success">
                        <i class="fa fa-check-circle"></i> Выбрана существующая позиция: {{ selectedItemLabel(line) }}.
                    </small>
                    <small v-else-if="line.item_query.trim()" class="form-text text-muted">
                        Будет создана новая позиция «{{ line.item_query.trim() }}».
                    </small>
                    <small v-else class="form-text text-muted">Можно выбрать существующую позицию или ввести название новой.</small>
                </div>

                <template v-if="!line.stock_item_id">
                    <div class="col-12 col-md-4 col-xl-2 form-group">
                        <label>
                            Категория позиции <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span>
                            <i class="fa fa-question-circle" data-toggle="tooltip" title="Имущество хранится, перемещается и вводится в эксплуатацию. Расходный материал хранится и списывается в расход."></i>
                        </label>
                        <select class="form-control" :name="'lines[' + i + '][new_item_kind]'" v-model="line.new_item_kind" @change="syncNewItemKind(line)">
                            <option v-for="(label, key) in itemKinds" :key="key" :value="key">{{ label }}</option>
                        </select>
                        <small class="form-text text-muted">{{ kindHelp(line.new_item_kind) }}</small>
                    </div>
                    <div class="col-12 col-md-4 col-xl-2 form-group">
                        <label>
                            Способ учета <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span>
                            <i class="fa fa-question-circle" data-toggle="tooltip" title="По количеству учитывается общий остаток. По экземплярам создается отдельная карточка для каждой единицы."></i>
                        </label>
                        <select class="form-control" :name="'lines[' + i + '][new_item_tracking_mode]'" v-model="line.new_item_tracking_mode" @change="syncUnitRows(line)">
                            <option value="quantity">По количеству</option>
                            <option v-if="line.new_item_kind === 'tracked_asset'" value="individual">По экземплярам</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-4 col-xl-2 form-group">
                        <label>
                            Единица учета <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span>
                            <i class="fa fa-question-circle" data-toggle="tooltip" title="Определяет, что означает количество: штуки, литры, килограммы, комплекты или упаковки."></i>
                        </label>
                        <input class="form-control" :name="'lines[' + i + '][new_item_unit_name]'" v-model="line.new_item_unit_name" list="stock-unit-options" placeholder="шт.">
                        <small class="form-text text-muted">Например: шт., л, кг.</small>
                    </div>
                </template>

                <div v-else class="col-12 col-xl-6 form-group">
                    <label>Параметры справочника</label>
                    <div class="form-control-plaintext">
                        <span class="badge badge-secondary mr-1">{{ kindLabel(effectiveKind(line)) }}</span>
                        <span class="badge badge-secondary mr-1">{{ trackingLabel(effectiveTracking(line)) }}</span>
                        <span class="badge badge-secondary">{{ effectiveUnit(line) }}</span>
                    </div>
                    <input type="hidden" :name="'lines[' + i + '][new_item_kind]'" value="">
                    <input type="hidden" :name="'lines[' + i + '][new_item_tracking_mode]'" value="">
                    <input type="hidden" :name="'lines[' + i + '][new_item_unit_name]'" value="">
                </div>
            </div>

            <template v-if="additionMode">
                <div v-if="line.cost_state === 'specified'" class="row purchase-recognition-row">
                    <div class="col-12 col-md-6 col-lg-3 form-group">
                        <label>Учет стоимости <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                        <select class="form-control js-recognition-action" :name="'lines[' + i + '][recognition_action]'" v-model="line.recognition_action" @change="syncRecognitionAction(line); triggerRecalc()">
                            <option v-if="line.receipt_destination !== 'immediate_consumption'" value="on_use">Учесть позже</option>
                            <option value="recognize_now">Учесть сейчас за выбранный период</option>
                            <option value="already_recognized">Стоимость уже была учтена ранее</option>
                        </select>
                    </div>
                    <template v-if="line.recognition_action === 'recognize_now'">
                        <div class="col-12 col-md-6 col-lg-3 form-group">
                            <label>Объект учета <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                            <property-picker
                                :name="'lines[' + i + '][recognition_property_id]'"
                                v-model="line.recognition_property_id"
                                :options="normalizedProperties"
                                :placeholder="relatedPlaceholder(line)"
                                picker-type="recognition"
                            ></property-picker>
                        </div>
                        <div class="col-12 col-md-6 col-lg-3 form-group">
                            <label>Группа расходов <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                            <select class="form-control" :name="'lines[' + i + '][expense_group]'" v-model="line.expense_group"><option v-for="(label, key) in expenseGroups" :key="key" :value="key">{{ label }}</option></select>
                        </div>
                        <div class="col-12 col-md-6 col-lg-3 form-group js-recognition-period-control"
                             :data-line-key="line.key"
                             :data-recognition-from="line.recognition_from"
                             :data-recognition-to="line.recognition_to">
                            <label :for="'receipt-recognition-period-' + line.key">Период учета <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                            <div class="input-group">
                                <div class="input-group-prepend"><span class="input-group-text"><i class="fa fa-calendar-alt"></i></span></div>
                                <input :id="'receipt-recognition-period-' + line.key" type="text" class="form-control js-recognition-period-range-input" autocomplete="off" placeholder="Выберите период учета" readonly>
                            </div>
                            <input type="hidden" :name="'lines[' + i + '][recognition_from]'" class="js-recognition-period-from" :value="line.recognition_from">
                            <input type="hidden" :name="'lines[' + i + '][recognition_to]'" class="js-recognition-period-to" :value="line.recognition_to">
                        </div>
                    </template>
                </div>
                <input v-else-if="line.cost_state === 'zero'" type="hidden" :name="'lines[' + i + '][recognition_action]'" value="not_applicable">
                <input v-else type="hidden" :name="'lines[' + i + '][recognition_action]'" value="pending_valuation">
            </template>

            <div class="row">
                <div class="col-12 col-sm-4 col-md-2 col-xl-2 form-group">
                    <label>Количество, {{ effectiveUnit(line) }} <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <input type="number" min="1" max="10000" step="1" class="form-control" :name="'lines[' + i + '][quantity]'" v-model.number="line.quantity" @input="onQuantityInput(line)">
                </div>
                <div class="col-12 col-sm-8 col-md-3 col-xl-2 form-group">
                    <label>Учет стоимости <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <select class="form-control js-cost-state" :name="'lines[' + i + '][cost_state]'" v-model="line.cost_state" @change="syncCostState(line); reviewPaymentChange(line)">
                        <option value="specified">Указать стоимость</option>
                        <option value="zero">Без стоимости</option>
                        <option value="unknown">Уточнить позже</option>
                    </select>
                </div>
                <div class="col-12 col-sm-6 col-md-3 col-xl-2 form-group">
                    <label>Цена за ед., руб.</label>
                    <input class="form-control js-unit-cost" v-model="line.unit_cost" :disabled="line.cost_state !== 'specified'" placeholder="0,00" @input="onUnitCostInput(line)">
                    <small v-if="unitCostRemainderInfo(line)" class="form-text text-muted">
                        <span class="js-unit-cost-hint">{{ unitCostRemainderInfo(line).display }}</span>
                        <i v-if="unitCostRemainderInfo(line).isApprox" :key="unitCostRemainderInfo(line).tooltip" class="fa fa-question-circle ml-1 js-unit-cost-approx-icon" data-toggle="tooltip" :title="unitCostRemainderInfo(line).tooltip"></i>
                    </small>
                    <small v-else class="form-text text-muted">Для расчета общей стоимости.</small>
                </div>
                <div class="col-12 col-sm-6 col-md-4 col-xl-3 form-group">
                    <label>Складская стоимость позиции, руб. <span v-if="line.cost_state === 'specified'" class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <input class="form-control js-line-cost" :name="'lines[' + i + '][line_cost]'" v-model="line.line_cost" :disabled="line.cost_state !== 'specified'" placeholder="0,00" @input="onLineCostInput(line)">
                    <small class="form-text text-muted">Стоимость всего количества в этой позиции.</small>
                </div>
                <div class="col-12 col-md-12 col-xl-3 form-group">
                    <label>Объект поступления <span v-if="isEdit" class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <property-picker
                        :name="'lines[' + i + '][receiving_property_id]'"
                        v-model="line.receiving_property_id"
                        :options="normalizedProperties"
                        :placeholder="receivingPlaceholder(line)"
                        picker-type="receiving"
                    ></property-picker>
                    <small class="form-text text-muted">Куда физически поступила эта позиция.</small>
                </div>
            </div>

            <div v-if="additionMode" class="border rounded p-3 mb-3 stock-line-payments">
                <input type="hidden" :name="'lines[' + i + '][payments_submitted]'" value="1">
                <input v-if="line.payment_snapshot" type="hidden" :name="'lines[' + i + '][payment_snapshot]'" :value="line.payment_snapshot">
                <div class="mb-2">
                    <strong>Фактические оплаты поставщику по позиции</strong>
                </div>
                <p class="small mb-2">Сумма оплат: <strong class="js-paid-total">{{ money(paidKopecks(line)) }} ₽</strong>. Признанная стоимость: <strong>{{ recognizedCost(line) }}</strong>. Ещё не учтённая стоимость: <strong class="js-undistributed-cost">{{ undistributedCost(line) }}</strong>.</p>
                <p v-if="line.cost_state === 'unknown' && paidKopecks(line)" class="alert alert-info mb-2">Стоимость неизвестна; оплату пока невозможно сравнить со стоимостью.</p>
                <div v-for="(payment, j) in line.payments" :key="payment.payment_key" class="row align-items-end">
                    <input v-if="payment.id" type="hidden" :name="'lines[' + i + '][payments][' + j + '][id]'" :value="payment.id">
                    <input type="hidden" :name="'lines[' + i + '][payments][' + j + '][payment_key]'" :value="payment.payment_key">
                    <div class="col-12 col-md-3 form-group"><label :for="'payment-amount-' + line.key + '-' + j">Сумма, руб.</label><input :id="'payment-amount-' + line.key + '-' + j" class="form-control js-payment-amount" :name="'lines[' + i + '][payments][' + j + '][amount]'" v-model="payment.amount" required @input="reviewPaymentChange(line)"></div>
                    <div class="col-12 col-md-3 form-group"><label :for="'payment-date-' + line.key + '-' + j">Дата оплаты</label><input :id="'payment-date-' + line.key + '-' + j" type="text" class="form-control js-purchase-payment-date" data-date-picker-enabled="true" data-date-picker-format="dd.mm.yyyy" data-date-format="dd.mm.yyyy" autocomplete="off" placeholder="ДД.ММ.ГГГГ" :data-line-key="line.key" :data-payment-key="payment.payment_key" :name="'lines[' + i + '][payments][' + j + '][date]'" v-model="payment.date" required></div>
                    <div class="col-12 col-md-4 form-group"><label>Объект ДДС</label><property-picker :name="'lines[' + i + '][payments][' + j + '][property_id]'" v-model="payment.property_id" :options="normalizedProperties" picker-type="cash" placeholder="Выберите объект ДДС"></property-picker></div>
                    <div class="col-12 col-md-2 form-group"><button type="button" class="btn btn-outline-danger" @click="removePayment(line, j)">Удалить</button></div>
                </div>
                <button type="button" class="btn btn-sm btn-outline-primary mb-2" @click="addPayment(line)">Добавить оплату</button>
                <template v-if="paidKopecks(line) > 0 && line.cost_state !== 'unknown'">
                    <div v-if="line.cost_state === 'zero'" class="alert alert-warning">Оплата при нулевой стоимости противоречит признаку «Без стоимости».</div>
                    <div v-if="signedDifference(line) !== 0" class="alert alert-warning js-payment-difference">Разница оплаты и стоимости: {{ signedDifference(line) > 0 ? '+' : '−' }}{{ money(Math.abs(signedDifference(line))) }} ₽</div>
                    <label v-if="signedDifference(line) !== 0" class="d-block"><input type="checkbox" :name="'lines[' + i + '][confirmed_difference_kopecks]'" :value="signedDifference(line)" :checked="line.reviewed_difference_kopecks === signedDifference(line)" @change="line.reviewed_difference_kopecks = $event.target.checked ? signedDifference(line) : null"> Подтверждаю разницу оплаты и стоимости {{ signedDifference(line) > 0 ? '+' : '−' }}{{ money(Math.abs(signedDifference(line))) }} ₽</label>
                    <label v-if="line.cost_state === 'zero'" class="d-block"><input type="checkbox" :name="'lines[' + i + '][confirm_zero_payment]'" value="1" :checked="line.reviewed_zero_payment_kopecks === paidKopecks(line)" @change="line.reviewed_zero_payment_kopecks = $event.target.checked ? paidKopecks(line) : null"> Подтверждаю оплату при нулевой стоимости</label>
                </template>
            </div>

            <div v-if="effectiveKind(line) === 'tracked_asset'" class="row">
                <div class="col-12 col-md-5 col-xl-4 form-group">
                    <label>Гарантия производителя до</label>
                    <input
                        type="text"
                        class="form-control js-purchase-warranty-date"
                        data-date-picker-enabled="true"
                        data-date-picker-format="dd.mm.yyyy"
                        data-date-format="dd.mm.yyyy"
                        autocomplete="off"
                        placeholder="ДД.ММ.ГГГГ"
                        :data-line-key="line.key"
                        :name="'lines[' + i + '][warranty_until]'"
                        v-model="line.warranty_until"
                    >
                    <small class="form-text text-muted">Одна дата для всего количества и всех экземпляров.</small>
                </div>
                <div class="col-12 col-md-7 col-xl-8 form-group">
                    <label class="d-block">Договорная опись</label>
                    <div class="form-check contract-inventory-check">
                        <input type="hidden" :name="'lines[' + i + '][include_in_contract_inventory]'" value="0">
                        <input :id="'contract-inventory-' + line.key" class="form-check-input" type="checkbox" :name="'lines[' + i + '][include_in_contract_inventory]'" value="1" v-model="line.include_in_contract_inventory">
                        <label class="form-check-label" :for="'contract-inventory-' + line.key">
                            Включать позицию в опись имущества объекта
                            <i class="fa fa-question-circle" data-toggle="tooltip" title="Позиция будет учитываться в описи имущества объекта и сможет попадать в акты приема-передачи арендатора."></i>
                        </label>
                    </div>
                    <small class="form-text text-muted">Настройка применяется ко всей позиции и всем создаваемым экземплярам.</small>
                </div>
            </div>
            <input v-else type="hidden" :name="'lines[' + i + '][include_in_contract_inventory]'" value="0">

            <div v-if="!storageOnly" class="row">
                <div :class="destinationColumnClass(line)" class="form-group">
                    <label>После поступления <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <select class="form-control" :name="'lines[' + i + '][receipt_destination]'" v-model="line.receipt_destination" @change="syncRecognitionDefaults(line)">
                        <option value="storage">Оставить на хранении</option>
                        <option v-if="effectiveKind(line) === 'tracked_asset'" value="immediate_use">Ввести в эксплуатацию</option>
                        <option v-if="effectiveKind(line) === 'consumable' && line.cost_state !== 'unknown'" value="immediate_consumption">Израсходовать материал</option>
                    </select>
                    <small v-if="line.receipt_destination === 'storage'" class="form-text text-muted">Позиция останется на выбранном объекте поступления.</small>
                </div>
                <div v-if="line.receipt_destination === 'immediate_use'" class="col-12 col-md-8 col-xl-5 form-group">
                    <label>Объект эксплуатации <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <property-picker
                        :name="'lines[' + i + '][use_property_id]'"
                        v-model="line.use_property_id"
                        :options="normalizedProperties"
                        :placeholder="relatedPlaceholder(line)"
                        picker-type="use"
                    ></property-picker>
                </div>
                <div v-if="line.receipt_destination === 'immediate_use' && !additionMode" class="col-12 col-md-4 col-xl-3 form-group">
                    <label>Дата ввода <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <input
                        type="text"
                        class="form-control js-purchase-operation-date"
                        data-date-picker-enabled="true"
                        data-date-picker-format="dd.mm.yyyy"
                        data-date-format="dd.mm.yyyy"
                        autocomplete="off"
                        placeholder="ДД.ММ.ГГГГ"
                        :data-line-key="line.key"
                        :name="'lines[' + i + '][use_date]'"
                        v-model="line.use_date"
                        @change="syncOperationDate(line, $event.target.value)"
                    >
                </div>
                <div v-else-if="line.receipt_destination === 'immediate_use'" class="col-12 col-md-4 col-xl-3 form-group">
                    <label>Дата ввода</label>
                    <p class="form-control-plaintext">Дата добавления</p>
                </div>
                <div v-if="line.receipt_destination === 'immediate_consumption'" class="col-12 col-md-7 col-xl-4 form-group">
                    <label>Объект затрат <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <property-picker
                        :name="'lines[' + i + '][consumption_property_id]'"
                        v-model="line.consumption_property_id"
                        :options="normalizedProperties"
                        :placeholder="relatedPlaceholder(line)"
                        picker-type="consumption"
                    ></property-picker>
                </div>
                <div v-if="line.receipt_destination === 'immediate_consumption' && !additionMode" class="col-12 col-md-5 col-xl-2 form-group">
                    <label>Дата расхода <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <input
                        type="text"
                        class="form-control js-purchase-operation-date"
                        data-date-picker-enabled="true"
                        data-date-picker-format="dd.mm.yyyy"
                        data-date-format="dd.mm.yyyy"
                        autocomplete="off"
                        placeholder="ДД.ММ.ГГГГ"
                        :data-line-key="line.key"
                        :name="'lines[' + i + '][consumption_date]'"
                        v-model="line.consumption_date"
                        @change="syncOperationDate(line, $event.target.value)"
                    >
                </div>
                <div v-else-if="line.receipt_destination === 'immediate_consumption'" class="col-12 col-md-5 col-xl-2 form-group">
                    <label>Дата расхода</label>
                    <p class="form-control-plaintext">Дата добавления</p>
                </div>
                <div v-if="line.receipt_destination === 'immediate_consumption'" class="col-12 col-xl-3 form-group">
                    <label>Причина</label>
                    <input class="form-control" :name="'lines[' + i + '][consumption_reason]'" v-model="line.consumption_reason" placeholder="Что израсходовано">
                </div>
            </div>

            <div v-if="!storageOnly && !additionMode && line.receipt_destination !== 'storage'" class="row purchase-recognition-row">
                <div class="col-12 col-md-4 form-group">
                    <label>Группа расходов <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span></label>
                    <select class="form-control" :name="'lines[' + i + '][expense_group]'" v-model="line.expense_group">
                        <option v-for="(label, key) in expenseGroups" :key="key" :value="key">{{ label }}</option>
                    </select>
                </div>
                <div class="col-12 col-md-6 form-group js-recognition-period-control"
                     :data-line-key="line.key"
                     :data-recognition-from="line.recognition_from"
                     :data-recognition-to="line.recognition_to">
                    <label>
                        <span data-toggle="tooltip" title="Период, к которому стоимость позиции относится в управленческом учете и KPI. Можно выбрать один день или диапазон.">Период учета <i class="fa fa-question-circle"></i></span>
                        <span class="text-danger" data-toggle="tooltip" title="Поле обязательно для заполнения">*</span>
                    </label>
                    <div class="input-group">
                        <div class="input-group-prepend"><span class="input-group-text"><i class="fa fa-calendar-alt"></i></span></div>
                        <input type="text" class="form-control js-recognition-period-range-input" autocomplete="off" placeholder="Выберите период учета" readonly>
                    </div>
                    <input type="hidden" :name="'lines[' + i + '][recognition_from]'" class="js-recognition-period-from" :value="line.recognition_from">
                    <input type="hidden" :name="'lines[' + i + '][recognition_to]'" class="js-recognition-period-to" :value="line.recognition_to">
                    <small class="form-text text-muted">По умолчанию стоимость учитывается днем ввода или списания.</small>
                </div>
            </div>

            <div v-if="effectiveTracking(line) === 'individual'" class="purchase-unit-panel border rounded mb-3">
                <div class="purchase-unit-panel-header d-flex justify-content-between align-items-center">
                    <strong>Экземпляры имущества</strong>
                    <span class="badge badge-primary">{{ normalizedQuantity(line) }}</span>
                </div>
                <div class="purchase-unit-panel-body">
                    <p class="text-muted mb-3">Для каждой единицы будет создана отдельная карточка. Серийные и инвентарные номера необязательны, их можно заполнить позднее.</p>
                    <div v-for="(unit, unitIndex) in line.units" :key="unitIndex" class="purchase-unit-row row align-items-end">
                        <div class="col-12 col-md-2 col-xl-1 form-group">
                            <strong class="purchase-unit-number">Экземпляр № {{ unitIndex + 1 }}</strong>
                        </div>
                        <div class="col-12 col-md-5 col-xl-5 form-group">
                            <label>Серийный номер производителя</label>
                            <input class="form-control" :name="'lines[' + i + '][units][' + unitIndex + '][serial_number]'" v-model="unit.serial_number">
                        </div>
                        <div class="col-12 col-md-5 col-xl-6 form-group">
                            <label>Инвентарный номер</label>
                            <input class="form-control" :name="'lines[' + i + '][units][' + unitIndex + '][inventory_number]'" v-model="unit.inventory_number">
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-group mb-0">
                <label>Примечание к позиции</label>
                <input class="form-control" :name="'lines[' + i + '][comment]'" v-model="line.comment" placeholder="Необязательное примечание к позиции">
            </div>

        </div>

        <datalist id="stock-unit-options">
            <option value="шт."></option>
            <option value="компл."></option>
            <option value="упак."></option>
            <option value="л"></option>
            <option value="кг"></option>
            <option value="м"></option>
            <option value="м²"></option>
        </datalist>

        <button type="button" class="btn btn-outline-primary mb-4" @click="addLine">
            <i class="fa fa-plus"></i> Добавить позицию
        </button>
    </div>
</template>

<script>
import PropertyPicker from './property-picker.vue';

export default {
    components: {
        PropertyPicker
    },
    props: {
        initialLines: {
            type: Array,
            default: () => []
        },
        items: {
            type: Array,
            default: () => []
        },
        properties: {
            type: [Array, Object],
            default: () => []
        },
        itemKinds: {
            type: Object,
            default: () => ({})
        },
        expenseGroups: {
            type: Object,
            default: () => ({})
        },
        receiptDate: {
            type: String,
            default: ''
        },
        storageOnly: {
            type: Boolean,
            default: false
        },
        additionMode: {
            type: Boolean,
            default: false
        },
        isEdit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            lines: [],
            nextKey: 1
        };
    },
    computed: {
        normalizedProperties() {
            if (Array.isArray(this.properties)) {
                return this.properties.map(item => ({
                    id: String(item.id !== undefined ? item.id : item.value),
                    name: String(item.name !== undefined ? item.name : item.label)
                }));
            }
            if (this.properties && typeof this.properties === 'object') {
                return Object.keys(this.properties).map(id => ({
                    id: String(id),
                    name: String(this.properties[id])
                }));
            }
            return [];
        }
    },
    created() {
        this.lines = this.initialLines && this.initialLines.length
            ? this.initialLines.map(line => this.hydrateLine(line))
            : [this.newLine()];
    },
    mounted() {
        this.lines.forEach(line => this.syncUnitRows(line));
        this.refreshTooltips();
        this.refreshDynamicControls();
        this.triggerRecalc();
    },
    updated() {
        this.refreshTooltips();
        this.refreshDynamicControls();
    },
    methods: {
        newLine() {
            const defaultDisplayDate = this.formatDate(this.receiptDate);

            return this.hydrateLine({
                is_saved: false,
                stock_item_id: '',
                new_item_name: '',
                new_item_kind: 'tracked_asset',
                new_item_tracking_mode: 'quantity',
                new_item_unit_name: 'шт.',
                quantity: 1,
                cost_state: 'specified',
                line_cost: '',
                unit_cost: '',
                last_cost_edit: 'line',
                payments: [],
                receiving_property_id: '',
                warranty_until: '',
                receipt_destination: 'storage',
                use_property_id: '',
                use_date: defaultDisplayDate,
                consumption_property_id: '',
                consumption_date: defaultDisplayDate,
                consumption_reason: '',
                expense_group: 'capex',
                recognition_action: 'on_use',
                recognition_property_id: '',
                recognition_from: this.normalizedDate(defaultDisplayDate) || this.receiptDate,
                recognition_to: this.normalizedDate(defaultDisplayDate) || this.receiptDate,
                recognition_dirty: false,
                units: [],
                comment: '',
                include_in_contract_inventory: false
            });
        },
        hydrateLine(source) {
            const defaultDisplayDate = this.formatDate(this.receiptDate);
            const line = Object.assign({
                stock_item_id: '',
                new_item_name: '',
                new_item_kind: 'tracked_asset',
                new_item_tracking_mode: 'quantity',
                new_item_unit_name: 'шт.',
                quantity: 1,
                cost_state: 'specified',
                line_cost: '',
                unit_cost: '',
                last_cost_edit: 'line',
                payments: [],
                confirm_zero_payment: false,
                confirmed_difference_kopecks: false,
                receiving_property_id: '',
                warranty_until: '',
                receipt_destination: 'storage',
                use_property_id: '',
                use_date: defaultDisplayDate,
                consumption_property_id: '',
                consumption_date: defaultDisplayDate,
                consumption_reason: '',
                expense_group: 'capex',
                recognition_action: 'on_use',
                recognition_property_id: '',
                recognition_from: this.normalizedDate(defaultDisplayDate) || this.receiptDate,
                recognition_to: this.normalizedDate(defaultDisplayDate) || this.receiptDate,
                recognition_dirty: false,
                units: [],
                comment: '',
                include_in_contract_inventory: false
            }, source || {});
            const item = this.itemById(line.stock_item_id);

            line.key = this.nextKey++;
            line.stock_item_id = line.stock_item_id ? String(line.stock_item_id) : '';
            line.new_item_kind = line.new_item_kind || 'tracked_asset';
            line.new_item_tracking_mode = line.new_item_tracking_mode || 'quantity';
            line.new_item_unit_name = line.new_item_unit_name || 'шт.';
            line.item_query = item ? item.name : (line.new_item_name || '');
            line.suggestions_open = false;
            line.receipt_destination = line.receipt_destination || 'storage';
            line.use_date = line.use_date ? this.formatDate(line.use_date) : defaultDisplayDate;
            line.consumption_date = line.consumption_date ? this.formatDate(line.consumption_date) : defaultDisplayDate;
            line.warranty_until = line.warranty_until ? this.formatDate(line.warranty_until) : '';
            line.expense_group = line.expense_group || (this.effectiveKind(line) === 'consumable' ? 'opex' : 'capex');
            line.recognition_action = line.recognition_action || (line.cost_state === 'specified' ? 'on_use' : (line.cost_state === 'zero' ? 'not_applicable' : 'pending_valuation'));
            line.recognition_property_id = line.recognition_property_id ? String(line.recognition_property_id) : '';
            line.receiving_property_id = line.receiving_property_id ? String(line.receiving_property_id) : '';
            line.use_property_id = line.use_property_id ? String(line.use_property_id) : '';
            line.consumption_property_id = line.consumption_property_id ? String(line.consumption_property_id) : '';
            line.is_saved = Boolean(this.isEdit && (source && (source.is_saved || source.id)));
            line.recognition_from = this.normalizedDate(line.recognition_from) || this.operationDate(line) || this.normalizedDate(this.receiptDate);
            line.recognition_to = this.normalizedDate(line.recognition_to) || line.recognition_from;
            line.recognition_dirty = !!line.recognition_dirty;
            line.include_in_contract_inventory = !!line.include_in_contract_inventory && line.include_in_contract_inventory !== '0';
            line.comment = line.comment ? String(line.comment) : '';
            line.payments = (Array.isArray(line.payments) ? line.payments : Object.values(line.payments || {})).map(payment => Object.assign({}, payment, {
                property_id: String(payment.property_id || ''),
                date: this.formatDate(payment.date) || this.todayFormatted()
            }));
            line.reviewed_difference_kopecks = Number(line.confirmed_difference_kopecks) || null;
            line.reviewed_zero_payment_kopecks = line.confirm_zero_payment ? line.payments.reduce((sum, payment) => sum + this.kopecks(payment.amount), 0) : null;
            const rawUnits = Array.isArray(line.units)
                ? line.units
                : (line.units && typeof line.units === 'object' ? Object.values(line.units) : []);
            line.units = rawUnits.map(unit => Object.assign({serial_number: '', inventory_number: ''}, unit || {}));
            line.last_cost_edit = (source && source.last_cost_edit) || 'line';
            if (line.line_cost && line.cost_state === 'specified') {
                const totalKop = this.kopecks(line.line_cost);
                const q = this.normalizedQuantity(line) || 1;
                line.unit_cost = (source && source.unit_cost) || this.money(Math.round(totalKop / q));
            } else {
                line.unit_cost = (source && source.unit_cost) || '';
            }

            return line;
        },
        addLine() {
            this.lines.push(this.newLine());
            this.$nextTick(() => {
                this.refreshTooltips();
                this.refreshDynamicControls();
                this.triggerRecalc();
            });
        },
        removeLine(index) {
            this.lines.splice(index, 1);
            this.$nextTick(this.triggerRecalc);
        },
        addPayment(line) {
            const uuid = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
                const value = Math.random() * 16 | 0;
                return (char === 'x' ? value : (value & 3 | 8)).toString(16);
            });
            const date = this.todayFormatted();
            line.payments.push({payment_key: uuid, amount: '', date, property_id: ''});
            this.reviewPaymentChange(line);
            this.$nextTick(() => {
                this.refreshDynamicControls();
            });
        },
        removePayment(line, index) {
            if (!window.confirm('Удалить ошибочную оплату поставщику?')) {
                return;
            }
            line.payments.splice(index, 1);
            this.reviewPaymentChange(line);
        },
        reviewPaymentChange(line) {
            line.reviewed_difference_kopecks = null;
            line.reviewed_zero_payment_kopecks = null;
            this.triggerRecalc();
        },
        kopecks(value) {
            const cleaned = String(value || '').replace(/\s/g, '').replace(',', '.');
            return /^\d+(\.\d{1,2})?$/.test(cleaned) ? Math.round(Number(cleaned) * 100) : 0;
        },
        money(value) {
            return (value / 100).toLocaleString('ru-RU', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        paidKopecks(line) {
            return line.payments.reduce((sum, payment) => sum + this.kopecks(payment.amount), 0);
        },
        signedDifference(line) {
            return this.paidKopecks(line) - (line.cost_state === 'specified' ? this.kopecks(line.line_cost) : 0);
        },
        recognizedCost(line) {
            return line.recognition_action === 'recognize_now' ? this.money(this.kopecks(line.line_cost)) + ' ₽' : (line.recognition_action === 'already_recognized' ? 'Учтена ранее' : 'Не признана сейчас');
        },
        undistributedCost(line) {
            if (line.cost_state === 'unknown') {
                return 'Неизвестна';
            }
            if (line.cost_state === 'zero') {
                return '0,00 ₽';
            }
            const lineCost = this.kopecks(line.line_cost);
            const recognized = line.recognition_action === 'recognize_now' || line.recognition_action === 'already_recognized' ? lineCost : 0;
            return this.money(Math.max(0, lineCost - recognized)) + ' ₽';
        },
        itemById(id) {
            return this.items.find(item => String(item.id) === String(id)) || null;
        },
        normalize(value) {
            return (value || '').toString().trim().toLocaleLowerCase('ru-RU').replace(/\s+/g, ' ');
        },
        itemSuggestions(line) {
            const query = this.normalize(line.item_query);
            if (!query) {
                return [];
            }

            return this.items.filter(item => item.is_active !== false && this.normalize(item.name).includes(query)).slice(0, 10);
        },
        updateItemQuery(line, value) {
            line.item_query = value;
            const selected = this.itemById(line.stock_item_id);
            if (!selected || selected.name !== value) {
                line.stock_item_id = '';
            }
            line.new_item_name = line.stock_item_id ? '' : value;
            line.suggestions_open = true;
            this.syncDestination(line);
            this.syncUnitRows(line);
        },
        selectItem(line, item) {
            line.stock_item_id = String(item.id);
            line.item_query = item.name;
            line.new_item_name = '';
            line.suggestions_open = false;
            line.expense_group = item.kind === 'consumable' ? 'opex' : 'capex';
            this.syncDestination(line);
            this.syncUnitRows(line);
        },
        closeSuggestions(line) {
            window.setTimeout(() => {
                line.suggestions_open = false;
            }, 100);
        },
        selectedItemLabel(line) {
            const item = this.itemById(line.stock_item_id);
            return item ? item.name : '';
        },
        effectiveKind(line) {
            const item = this.itemById(line.stock_item_id);
            return item ? item.kind : line.new_item_kind;
        },
        effectiveTracking(line) {
            const item = this.itemById(line.stock_item_id);
            return item ? item.tracking_mode : line.new_item_tracking_mode;
        },
        effectiveUnit(line) {
            const item = this.itemById(line.stock_item_id);
            return (item ? item.unit_name : line.new_item_unit_name) || 'шт.';
        },
        kindLabel(kind) {
            return this.itemKinds[kind] || kind;
        },
        trackingLabel(mode) {
            return mode === 'individual' ? 'По экземплярам' : 'По количеству';
        },
        kindHelp(kind) {
            return kind === 'tracked_asset'
                ? 'Мебель, техника и другое имущество, которое хранится или вводится в эксплуатацию.'
                : 'Материалы, которые хранятся и затем списываются в расход.';
        },
        destinationColumnClass(line) {
            if (line.receipt_destination === 'immediate_consumption') {
                return 'col-12 col-md-12 col-xl-3';
            }

            return line.receipt_destination === 'immediate_use'
                ? 'col-12 col-md-12 col-xl-4'
                : 'col-12 col-md-4';
        },
        syncNewItemKind(line) {
            if (line.new_item_kind === 'consumable') {
                line.new_item_tracking_mode = 'quantity';
                line.include_in_contract_inventory = false;
            }
            this.syncDestination(line);
            line.expense_group = this.effectiveKind(line) === 'consumable' ? 'opex' : 'capex';
            this.syncUnitRows(line);
        },
        syncDestination(line) {
            if (this.storageOnly) {
                line.receipt_destination = 'storage';
                return;
            }
            const kind = this.effectiveKind(line);
            if (kind === 'tracked_asset' && line.receipt_destination === 'immediate_consumption') {
                line.receipt_destination = 'storage';
            }
            if (kind === 'consumable' && line.receipt_destination === 'immediate_use') {
                line.receipt_destination = 'storage';
            }
        },
        receivingPlaceholder(line) {
            return this.isEdit ? 'Выберите объект' : 'Основной объект поступления';
        },
        relatedPlaceholder(line) {
            return line && line.is_saved ? 'Выберите объект' : 'Объект поступления позиции';
        },
        syncCostState(line) {
            if (line.cost_state !== 'specified') {
                line.line_cost = '';
                line.unit_cost = '';
            }
            if (line.cost_state === 'unknown' && line.receipt_destination === 'immediate_consumption') {
                line.receipt_destination = 'storage';
            }
            line.recognition_action = line.cost_state === 'specified' ? 'on_use' : (line.cost_state === 'zero' ? 'not_applicable' : 'pending_valuation');
            this.triggerRecalc();
            this.$nextTick(this.triggerRecalc);
        },
        syncRecognitionAction(line) {
            if (line.recognition_action !== 'recognize_now') {
                return;
            }
            if (!line.recognition_property_id) {
                line.recognition_property_id = '';
            }
            const date = this.normalizedDate(this.receiptDate);
            line.recognition_from = date;
            line.recognition_to = date;
        },
        todayFormatted() {
            const today = new Date();
            return `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;
        },
        formatDate(value) {
            if (!value) {
                return '';
            }
            if (window.formatDateForDisplay) {
                return window.formatDateForDisplay(value);
            }
            const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (match) {
                return `${match[3]}.${match[2]}.${match[1]}`;
            }

            return value;
        },
        normalizedDate(value) {
            if (!value) {
                return '';
            }
            if (window.normalizeDateForSubmit) {
                return window.normalizeDateForSubmit(value);
            }
            const match = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
            if (match) {
                return `${match[3]}-${match[2]}-${match[1]}`;
            }

            return value;
        },
        operationDate(line, changedValue) {
            const value = changedValue || (line.receipt_destination === 'immediate_consumption' ? line.consumption_date : line.use_date);

            return this.normalizedDate(value);
        },
        syncRecognitionDefaults(line) {
            if (line.cost_state === 'unknown' && line.receipt_destination === 'immediate_consumption') {
                line.receipt_destination = 'storage';
                return;
            }
            if (line.receipt_destination === 'storage') {
                return;
            }
            if (line.receipt_destination === 'immediate_consumption' && line.cost_state === 'specified' && line.recognition_action === 'on_use') {
                line.recognition_action = 'recognize_now';
                this.syncRecognitionAction(line);
            }
            line.expense_group = this.effectiveKind(line) === 'consumable' ? 'opex' : 'capex';
            line.recognition_dirty = false;
            this.syncOperationDate(line);
        },
        syncOperationDate(line, changedValue) {
            if (changedValue) {
                const formatted = this.formatDate(changedValue);
                if (line.receipt_destination === 'immediate_consumption') {
                    line.consumption_date = formatted;
                } else {
                    line.use_date = formatted;
                }
            }
            const date = this.operationDate(line, changedValue) || this.normalizedDate(this.receiptDate);
            if (!line.recognition_dirty) {
                line.recognition_from = date;
                line.recognition_to = date;
                this.$nextTick(() => this.updateRecognitionControl(line));
            }
        },
        updateRecognitionControl(line) {
            const control = window.$(this.$el).find('.js-recognition-period-control[data-line-key="' + line.key + '"]');
            if (control.length && window.updateRecognitionPeriodRange) {
                window.updateRecognitionPeriodRange(control, line.recognition_from, line.recognition_to);
            }
        },
        refreshDynamicControls() {
            this.$nextTick(() => {
                if (!window.$ || !window.initDynamicFormControls) {
                    return;
                }
                window.initDynamicFormControls(this.$el);
                window.$(this.$el).find('.js-purchase-operation-date').off('changeDate.purchase change.purchase input.purchase').on('changeDate.purchase change.purchase input.purchase', event => {
                    const input = window.$(event.currentTarget);
                    const line = this.lines.find(candidate => String(candidate.key) === String(input.data('line-key')));
                    if (!line) {
                        return;
                    }
                    const val = input.val();
                    if (val) {
                        if (line.receipt_destination === 'immediate_consumption') {
                            line.consumption_date = val;
                        } else {
                            line.use_date = val;
                        }
                    }
                    this.syncOperationDate(line, val);
                });
                window.$(this.$el).find('.js-purchase-warranty-date').off('changeDate.purchase change.purchase input.purchase').on('changeDate.purchase change.purchase input.purchase', event => {
                    const input = window.$(event.currentTarget);
                    const line = this.lines.find(candidate => String(candidate.key) === String(input.data('line-key')));
                    if (line) {
                        line.warranty_until = input.val();
                    }
                });
                window.$(this.$el).find('.js-purchase-payment-date').off('changeDate.purchase change.purchase input.purchase').on('changeDate.purchase change.purchase input.purchase', event => {
                    const input = window.$(event.currentTarget);
                    const line = this.lines.find(candidate => String(candidate.key) === String(input.data('line-key')));
                    const paymentKey = String(input.data('payment-key') || '');
                    if (line && line.payments) {
                        const payment = line.payments.find(p => String(p.payment_key) === paymentKey);
                        if (payment) {
                            payment.date = input.val();
                        }
                    }
                });
                window.$(this.$el).find('.js-recognition-period-range-input').off('apply.daterangepicker.purchase').on('apply.daterangepicker.purchase', event => {
                    const control = window.$(event.currentTarget).closest('.js-recognition-period-control');
                    const line = this.lines.find(candidate => String(candidate.key) === String(control.data('line-key')));
                    if (!line) {
                        return;
                    }
                    line.recognition_from = control.find('.js-recognition-period-from').val();
                    line.recognition_to = control.find('.js-recognition-period-to').val();
                    line.recognition_dirty = true;
                });
            });
        },
        normalizedQuantity(line) {
            const quantity = parseInt(line.quantity, 10);
            return Number.isFinite(quantity) && quantity > 0 ? Math.min(quantity, 10000) : 0;
        },
        syncUnitRows(line) {
            const quantity = this.normalizedQuantity(line);
            if (this.effectiveTracking(line) !== 'individual') {
                return;
            }
            while (line.units.length < quantity) {
                line.units.push({serial_number: '', inventory_number: ''});
            }
            if (line.units.length > quantity) {
                line.units.splice(quantity);
            }
        },
        parseKopecks(value) {
            let cleaned = String(value || '').replace(/\s/g, '').replace(',', '.');
            if (!cleaned) {
                return null;
            }
            if (cleaned.startsWith('.')) {
                cleaned = '0' + cleaned;
            }
            if (/^\d+(\.\d+)?$/.test(cleaned)) {
                return Math.round(Number(cleaned) * 100);
            }
            if (/^\d+\.$/.test(cleaned)) {
                return Math.round(Number(cleaned.slice(0, -1)) * 100);
            }
            return null;
        },
        onUnitCostInput(line) {
            line.last_cost_edit = 'unit';
            const trimmed = String(line.unit_cost || '').trim();
            if (!trimmed) {
                line.line_cost = '';
                this.reviewPaymentChange(line);
                return;
            }
            const unitKop = this.parseKopecks(line.unit_cost);
            if (unitKop !== null) {
                const q = this.normalizedQuantity(line) || 1;
                line.line_cost = this.money(unitKop * q);
            }
            this.reviewPaymentChange(line);
        },
        onLineCostInput(line) {
            line.last_cost_edit = 'line';
            const trimmed = String(line.line_cost || '').trim();
            if (!trimmed) {
                line.unit_cost = '';
                this.reviewPaymentChange(line);
                return;
            }
            const totalKop = this.parseKopecks(line.line_cost);
            if (totalKop !== null) {
                const q = this.normalizedQuantity(line) || 1;
                line.unit_cost = this.money(Math.round(totalKop / q));
            }
            this.reviewPaymentChange(line);
        },
        onQuantityInput(line) {
            this.syncUnitRows(line);
            if (line.cost_state === 'specified') {
                const q = this.normalizedQuantity(line) || 1;
                if (line.last_cost_edit === 'unit') {
                    const unitKop = this.parseKopecks(line.unit_cost);
                    if (unitKop !== null) {
                        line.line_cost = this.money(unitKop * q);
                    }
                } else {
                    const totalKop = this.parseKopecks(line.line_cost);
                    if (totalKop !== null) {
                        line.unit_cost = this.money(Math.round(totalKop / q));
                    }
                }
            }
            this.reviewPaymentChange(line);
        },
        unitCostRemainderInfo(line) {
            if (line.cost_state !== 'specified' || !line.line_cost) {
                return null;
            }
            const totalKop = this.parseKopecks(line.line_cost);
            if (totalKop === null) {
                return null;
            }
            const q = this.normalizedQuantity(line) || 1;
            const unit = this.effectiveUnit(line) || 'ед.';
            const remainder = totalKop % q;
            const base = Math.floor(totalKop / q);
            const isApprox = remainder > 0;
            const unitCostDisplay = this.money(Math.round(totalKop / q));
            const display = (isApprox ? '≈ ' : '') + unitCostDisplay + ' ₽ / ' + unit;

            let tooltip = '';
            if (isApprox) {
                const baseCount = q - remainder;
                const remainderCount = remainder;
                const baseCost = this.money(base);
                const nextCost = this.money(base + 1);
                const totalFormatted = this.money(totalKop);
                tooltip = `Сумма делится с остатком: ${baseCount} ${unit} по ${baseCost} ₽ и ${remainderCount} ${unit} по ${nextCost} ₽. В учете сохранится точная общая сумма ${totalFormatted} ₽.`;
            }

            return {
                isApprox,
                display,
                tooltip
            };
        },
        refreshTooltips() {
            this.$nextTick(() => {
                if (window.$ && window.$.fn.tooltip) {
                    window.$(this.$el).find('[data-toggle="tooltip"]').tooltip();
                }
            });
        },
        triggerRecalc() {
            if (window.$) {
                this.$nextTick(() => window.$(document).trigger('stock:purchase-lines-changed'));
            }
        }
    }
};
</script>

<style scoped>
.purchase-receipt-line,
.stock-addition-line {
    color: rgba(255, 255, 255, 0.88);
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.14) !important;
}

.purchase-receipt-line > .row + .row,
.stock-addition-line > .row + .row {
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.purchase-receipt-line > .row .text-muted:not(.purchase-item-suggestion-meta),
.purchase-receipt-line > .row .form-text:not(.purchase-item-suggestion-meta),
.stock-addition-line > .row .text-muted:not(.purchase-item-suggestion-meta),
.stock-addition-line > .row .form-text:not(.purchase-item-suggestion-meta) {
    color: rgba(255, 255, 255, 0.65) !important;
}

.purchase-item-suggestions {
    right: 15px;
    left: 15px;
    width: auto !important;
    z-index: 20;
    max-height: 260px;
    overflow-y: auto;
    background-color: #fff;
    border: 1px solid #d9e0e7;
}

.purchase-item-suggestions .list-group-item {
    color: #2d353c;
    background-color: #fff;
    border-color: #d9e0e7;
}

.purchase-receipt-line > .row .purchase-item-suggestions .purchase-item-suggestion-meta,
.purchase-receipt-line > .row .purchase-item-suggestions .text-muted,
.purchase-receipt-line .purchase-item-suggestions .purchase-item-suggestion-meta,
.purchase-receipt-line .purchase-item-suggestions .text-muted,
.purchase-item-suggestions .purchase-item-suggestion-meta,
.purchase-item-suggestions .text-muted {
    color: #495057 !important;
}

.purchase-item-suggestions .list-group-item:hover,
.purchase-item-suggestions .list-group-item:focus {
    color: #fff;
    background-color: #348fe2;
}

.purchase-receipt-line > .row .purchase-item-suggestions .list-group-item:hover .purchase-item-suggestion-meta,
.purchase-receipt-line > .row .purchase-item-suggestions .list-group-item:focus .purchase-item-suggestion-meta,
.purchase-receipt-line > .row .purchase-item-suggestions .list-group-item:hover .text-muted,
.purchase-receipt-line > .row .purchase-item-suggestions .list-group-item:focus .text-muted,
.purchase-receipt-line .purchase-item-suggestions .list-group-item:hover .purchase-item-suggestion-meta,
.purchase-receipt-line .purchase-item-suggestions .list-group-item:focus .purchase-item-suggestion-meta,
.purchase-receipt-line .purchase-item-suggestions .list-group-item:hover .text-muted,
.purchase-receipt-line .purchase-item-suggestions .list-group-item:focus .text-muted,
.purchase-item-suggestions .list-group-item:hover .purchase-item-suggestion-meta,
.purchase-item-suggestions .list-group-item:focus .purchase-item-suggestion-meta,
.purchase-item-suggestions .list-group-item:hover .text-muted,
.purchase-item-suggestions .list-group-item:focus .text-muted {
    color: rgba(255, 255, 255, 0.85) !important;
}

.purchase-unit-panel {
    color: rgba(255, 255, 255, 0.88);
    background: rgba(0, 0, 0, 0.16);
    border-color: rgba(255, 255, 255, 0.1) !important;
}

.purchase-unit-panel-header {
    padding: 10px 15px;
    background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.purchase-unit-panel-body {
    padding: 15px;
}

.purchase-unit-panel .text-muted {
    color: rgba(255, 255, 255, 0.65) !important;
}

.purchase-unit-panel label,
.purchase-unit-panel strong {
    color: rgba(255, 255, 255, 0.92);
}

.purchase-unit-row + .purchase-unit-row {
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.purchase-unit-number {
    display: block;
    padding-bottom: 10px;
    white-space: nowrap;
}

.contract-inventory-check {
    min-height: 34px;
    padding-top: 7px;
}

@media (min-width: 768px) {
    .purchase-unit-number {
        padding-bottom: 8px;
    }
}
</style>
